import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { CollectionView } from '@1qa/shared';
import { api } from '@/lib/api';
import type { SelectGroup } from '@/components/ui';

/** Collections for a dropdown, organization ones and project ones under their own headings. */
export function collectionGroups(collections: CollectionView[]): SelectGroup[] {
  const opt = (c: CollectionView) => ({ value: c.id, label: c.name });
  return [
    { label: 'Organization', options: collections.filter((c) => c.scope === 'ORG').map(opt) },
    { label: 'This project', options: collections.filter((c) => c.scope === 'PROJECT').map(opt) },
  ];
}

const prefKey = (projectId: string) => `1qa.runCollection.${projectId}`;
const CHANGED = '1qa:run-collection';

function readPref(projectId: string): string | null {
  try {
    return window.localStorage.getItem(prefKey(projectId)) || null;
  } catch {
    return null;
  }
}

function writePref(projectId: string, id: string | null) {
  try {
    if (id) window.localStorage.setItem(prefKey(projectId), id);
    else window.localStorage.removeItem(prefKey(projectId));
  } catch {
    /* storage unavailable: keep the choice for this page only */
  }
}

/**
 * The test data collection the person runs this project's tests, groups and flows with (e.g.
 * Staging), remembered per project in this browser and shared by every Run button. A collection that
 * no longer exists falls back to the default values (null).
 */
export function useRunCollection(projectId: string | undefined) {
  const collections = useQuery({
    queryKey: ['project-collections', projectId],
    queryFn: () => api.projectCollections(projectId!),
    enabled: !!projectId,
  });
  const [stored, setStored] = useState<string | null>(() => (projectId ? readPref(projectId) : null));

  useEffect(() => {
    if (!projectId) return;
    setStored(readPref(projectId));
    const sync = (e: Event) => {
      if ((e as CustomEvent<string>).detail === projectId) setStored(readPref(projectId));
    };
    window.addEventListener(CHANGED, sync);
    return () => window.removeEventListener(CHANGED, sync);
  }, [projectId]);

  const set = useCallback(
    (id: string | null) => {
      if (!projectId) return;
      writePref(projectId, id);
      setStored(id);
      window.dispatchEvent(new CustomEvent(CHANGED, { detail: projectId }));
    },
    [projectId],
  );

  const list = collections.data ?? [];
  const current = list.find((c) => c.id === stored) ?? null;
  return { collections: list, collectionId: current?.id ?? null, collection: current, setCollectionId: set };
}

export type RunCollectionState = ReturnType<typeof useRunCollection>;
